import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { MapPin, Loader2, CheckCircle2, XCircle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "@/contexts/LocationContext";

const POPULAR = [
  { pincode: "110001", label: "New Delhi" },
  { pincode: "400001", label: "Mumbai" },
  { pincode: "560001", label: "Bangalore" },
  { pincode: "600001", label: "Chennai" },
  { pincode: "500001", label: "Hyderabad" },
  { pincode: "700001", label: "Kolkata" },
  { pincode: "411001", label: "Pune" },
  { pincode: "380001", label: "Ahmedabad" },
];

export default function LocationDialog() {
  const { open, closeLocation, lookup, location } = useLocation();
  const [pincode, setPincode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setPincode(location?.pincode ?? "");
      setResult(null);
    }
  }, [open, location]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setResult(null);
    try {
      const res = await lookup(pincode);
      if (res.ok) {
        setResult(res.data);
        if (res.data?.serviceable) {
          toast.success(`Deliverable to ${res.data.city}${res.data.state ? ", " + res.data.state : ""}`);
          setTimeout(() => closeLocation(), 900);
        } else {
          toast.error("Sorry, we don't deliver to that pincode yet.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const useGeolocation = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Location is not available in this browser");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { Accept: "application/json" } }
          );
          const data = await res.json();
          const addr = data?.address || {};
          const code = addr.postcode?.replace(/\D/g, "").slice(0, 6);
          if (code && code.length === 6) {
            setPincode(code);
            const r = await lookup(code);
            if (r.ok && r.data?.serviceable) {
              toast.success(`Detected ${r.data.city}${r.data.state ? ", " + r.data.state : ""}`);
              setTimeout(() => closeLocation(), 900);
            }
            return;
          }
          // Fallback: no postcode in reverse geocode result. Try to derive the
          // city from the coords via the local range map by testing a few
          // representative pincodes for the returned city name (Nominatim
          // usually surfaces suburb/city/state even when postcode is absent).
          const nominatimCity =
            addr.city || addr.town || addr.village || addr.suburb || addr.neighbourhood || addr.county || "";
          const state = addr.state || "";
          if (nominatimCity) {
            const inferred = { pincode: "—", city: nominatimCity, state, serviceable: true };
            // Persist the detected city so the topbar updates even without a pincode.
            const applied = await lookup("110001"); // just to seed cache
            void applied;
            // Reuse the location provider by mimicking a successful lookup.
            const evt = new CustomEvent("dyntra-location-set", { detail: inferred });
            window.dispatchEvent(evt);
            toast.success(`Detected ${nominatimCity}${state ? ", " + state : ""}`);
            setTimeout(() => closeLocation(), 900);
          } else {
            toast.error("Couldn't detect a pincode from your location");
          }
        } catch {
          toast.error("Location lookup failed");
        } finally {
          setLoading(false);
        }
      },
      () => {
        toast.error("Location permission denied");
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeLocation(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>Deliver to</DialogTitle>
          <DialogDescription>Enter your pincode to check delivery availability.</DialogDescription>
        </VisuallyHidden>

        <div className="bg-[#4B2AAD] text-white p-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
            <MapPin className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-tight">Deliver to</p>
            <p className="text-xs text-white/80">Check pincode serviceability & ETA</p>
          </div>
        </div>

        <form onSubmit={submit} className="p-5">
          <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Enter 6-digit pincode</label>
          <div className="mt-1 flex gap-2">
            <input
              data-testid="location-pincode-input"
              autoFocus
              inputMode="numeric"
              maxLength={6}
              value={pincode}
              onChange={(e) => { setPincode(e.target.value.replace(/\D/g, "").slice(0, 6)); setResult(null); }}
              placeholder="e.g. 110001"
              className="flex-1 rounded-xl border border-gray-200 focus:border-[#4B2AAD] focus:ring-2 focus:ring-[#4B2AAD]/15 px-4 py-2.5 outline-none text-sm tracking-widest font-bold"
            />
            <button
              type="submit"
              data-testid="location-check-btn"
              disabled={loading || pincode.length !== 6}
              className="dyntra-btn-primary min-w-[92px] justify-center"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
            </button>
          </div>

          <button
            type="button"
            data-testid="location-geolocation-btn"
            onClick={useGeolocation}
            disabled={loading}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4B2AAD] hover:underline"
          >
            <Navigation className="h-3.5 w-3.5" /> Use my current location
          </button>

          {result && (
            <div
              data-testid="location-result"
              className={`mt-4 rounded-xl px-4 py-3 text-sm border ${result.serviceable ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}
            >
              <div className="flex items-center gap-2 font-semibold">
                {result.serviceable ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {result.serviceable
                  ? `Great! We deliver to ${result.city}${result.state ? ", " + result.state : ""}.`
                  : "Sorry, we don't deliver here yet."}
              </div>
              {result.serviceable && (
                <p className="text-xs mt-1 opacity-80">Free delivery in 3–5 business days. Same-city same-day in select metros.</p>
              )}
            </div>
          )}

          <div className="mt-5">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Popular cities</p>
            <div className="flex flex-wrap gap-1.5">
              {POPULAR.map((p) => (
                <button
                  key={p.pincode}
                  type="button"
                  data-testid={`location-popular-${p.pincode}`}
                  onClick={() => setPincode(p.pincode)}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white hover:bg-[#F3EFFC] hover:border-[#4B2AAD]/30 px-3 py-1 text-xs font-semibold text-gray-700 transition-colors"
                >
                  {p.label} · {p.pincode}
                </button>
              ))}
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
